import * as v from 'valibot'
import { fdaXmlParseResultSchema } from '../../xml'

export enum LLMProvider {
  OPENAI = 'openai',
  GEMINI = 'gemini',
  ANTHROPIC = 'anthropic',
}

export interface LLMConfig {
  llmProvider: LLMProvider
  model?: string
  apiKey?: string
}

// Activity Feed Types
// ----------------------------------

export enum ActivityFeedType {
  'ai_thought' = 'ai_thought',
  'chat' = 'chat',
  'comment' = 'comment',
  'draft_change' = 'draft_change',
  'error' = 'error',
  'question' = 'question',
  'message' = 'message',
  'note' = 'note',
  'notification' = 'notification',
  'status_update' = 'status_update',
  'suggestion' = 'suggestion',
  'version' = 'version',
  'workflow' = 'workflow',
}

export type ActivityFeedItemType =
  | 'ai_thought'
  | 'chat'
  | 'comment'
  | 'draft_change'
  | 'error'
  | 'question'
  | 'message'
  | 'note'
  | 'notification'
  | 'status_update'
  | 'suggestion'
  | 'version'
  | 'workflow'

const baseFeedItemSchema = v.object({
  id: v.string(),
  type: v.enum(ActivityFeedType),
  message: v.optional(v.string()),
  data: v.optional(v.any()),
  subject: v.optional(v.string()),
  actor: v.string(),
  createdAt: v.string(),
  updatedAt: v.string(),
})
export type BaseFeedItem = v.InferOutput<typeof baseFeedItemSchema>

const suggestionSchema = v.object({
  ...baseFeedItemSchema.entries,
  type: v.literal(ActivityFeedType.suggestion),
  data: v.object({
    fieldPath: v.string(),
    value: v.string(),
    reason: v.string(),
    confidence: v.number(),
  }),
})
export type Suggestion = v.InferOutput<typeof suggestionSchema>

export type ActivityFeedItem = BaseFeedItem | Suggestion

// Document types
// ----------------------------------
export const documentSchema = v.object({
  id: v.string(),
  name: v.string(),

  hash: v.string(),
  fileType: v.picklist(['text/xml', 'application/xml', 'application/pdf']),
  source: v.optional(v.picklist(['fda', 'payer', 'research'])),

  content: v.optional(v.union([v.string(), v.any()])), // Stored separately in r2 bucket as ArrayBuffer
  size: v.number(),
  lastModified: v.number(),

  isPrimary: v.optional(v.boolean()),
  structuredMetadata: v.optional(fdaXmlParseResultSchema), // Stored separately in r2 bucket as JSON
  metadata: v.optional(
    v.object({
      citation: v.optional(v.string()),
      effectiveTime: v.optional(v.string()),
    }),
  ),

  createdAt: v.string(),
  updatedAt: v.string(),
})
export type Document = v.InferOutput<typeof documentSchema>

// Indication types
// ----------------------------------
export enum IndicationType {
  ON_LABEL = 'ON_LABEL',
  OFF_LABEL = 'OFF_LABEL',
}

/** @deprecated Use IndicationType instead */
export const IndicationTypeEnum = IndicationType

export enum LevelOfEvidence {
  CATEGORY_1 = 'CATEGORY_1',
  CATEGORY_2A = 'CATEGORY_2A',
  CATEGORY_2B = 'CATEGORY_2B',
  CATEGORY_3 = 'CATEGORY_3',
}

const TIME_PERIOD_VALUES = ['day', 'week', 'month', 'year'] as const
export type TimePeriod = (typeof TIME_PERIOD_VALUES)[number]
export const timePeriodSchema = v.picklist(TIME_PERIOD_VALUES)

const INDICATION_STATUS_VALUES = [
  'draft',
  'for_review',
  'approved',
  'excluded',
  'suggested',
  'completed',
] as const
export type IndicationStatus = (typeof INDICATION_STATUS_VALUES)[number]
export const indicationStatusSchema = v.picklist(INDICATION_STATUS_VALUES)

/* Legacy admin forms stored numeric fields as strings ("5"); coerce them on parse. */
const numericSchema = v.union([
  v.number(),
  v.pipe(v.string(), v.trim(), v.decimal(), v.transform(Number)),
])

export const dosageCalculationSchema = v.object({
  // Max HCPCS Units (per day)
  maxDailyHcpcsUnits: v.optional(numericSchema),

  // Max Frequency
  frequencyMaxAdminDays: v.optional(numericSchema),
  frequencyMaxFrequency: v.optional(numericSchema),
  frequencyTimePeriod: v.optional(v.nullable(timePeriodSchema)),

  // Max HCPCS Units Over Time
  maxUnitsOverTimeMaxHCPCSUnits: v.optional(numericSchema),
  maxUnitsOverHcpcsMaxFrequency: v.optional(numericSchema),
  maxUnitsOverHcpcsTimePeriod: v.optional(v.nullable(timePeriodSchema)),
})

export const PAYER_CLASSIFICATION_VALUES = [
  'corroborates_baseline',
  'contradicts_baseline',
  'net_new_explicit',
  'net_new_ambiguous',
] as const
export type PayerClassification = (typeof PAYER_CLASSIFICATION_VALUES)[number]
export const payerClassificationSchema = v.picklist(PAYER_CLASSIFICATION_VALUES)

export const payerSourceSchema = v.object({
  documentId: v.string(),
  classification: payerClassificationSchema,
  evidence: v.string(),
})
export type PayerSource = v.InferOutput<typeof payerSourceSchema>

export const icd10CodeSchema = v.object({
  code: v.string(), // Single code or range (e.g., "J12.89" or "C00-C14")
  sourceReference: v.optional(v.string()),
  isSuggested: v.optional(v.boolean()),
  isRange: v.optional(v.boolean()),
  isRemoved: v.optional(v.boolean()),
  isSecondary: v.optional(v.boolean()),
  isManuallyAdded: v.optional(v.boolean()),
  reasoning: v.optional(v.string()),
  /**
   * Per-payer-document signals for this code. Absence (or empty array) means
   * no payer has been processed for this code yet. Populated by the
   * `process-payer-docs` workflow step via `upsertIcd10Codes`.
   */
  payerSources: v.optional(v.array(payerSourceSchema)),
  /**
   * Explicit pharmacist decision on whether this code belongs in the defined
   * ICD-10 list for the indication. Wins over the suggestion-tree placement
   * and over `isManuallyAdded`. Absence means the code follows the
   * categorization reducer's default rules. Set via the
   * `PATCH /indications/:id/icd10-codes/:code` route.
   */
  acceptanceState: v.optional(v.picklist(['included', 'excluded'])),
  /**
   * Free-text pharmacist note explaining why a code was excluded. Only
   * meaningful when `acceptanceState === 'excluded'`. Optional in v1.
   */
  exclusionNote: v.optional(v.string()),
})
export type IndicationIcd10Code = v.InferOutput<typeof icd10CodeSchema>

/** HCPCS code schema for LLM extraction results (worker-internal) */
export const hcpcsCodeSchema = v.object({
  code: v.string(), // The J-code (e.g., "J1234")
  description: v.string(), // NLM descriptor text
  confidence: v.pipe(v.number(), v.minValue(0), v.maxValue(100)), // 0-100: scoring confidence from search algorithm
  sourceQuery: v.optional(v.string()),
})

export const indicationSchema = v.object({
  id: v.string(),
  name: v.string(),
  description: v.optional(v.string()),
  type: v.optional(v.enum(IndicationType)),
  status: v.optional(indicationStatusSchema),
  /* ponytail: legacy evidence grades are unused metadata, not capture blockers.
     Keep new extraction strict until a clinical mapping is defined. */
  levelOfEvidence: v.optional(
    v.fallback(v.nullable(v.enum(LevelOfEvidence)), null),
  ),
  confidence: v.optional(v.number()),

  // Age
  age: v.optional(v.nullable(v.string())),
  ageMin: v.optional(v.nullable(numericSchema)),
  ageMinUom: v.optional(v.nullable(timePeriodSchema)),
  ageMax: v.optional(v.nullable(numericSchema)),
  ageMaxUom: v.optional(v.nullable(timePeriodSchema)),

  // Metadata
  sourceDocumentId: v.optional(
    v.pipe(
      v.nullable(v.string()),
      v.description('ID of the document that generated this indication'),
    ),
  ),
  sourcePageNumber: v.optional(
    v.pipe(
      v.nullable(v.number()),
      v.description(
        'Page number of the document that generated this indication',
      ),
    ),
  ),
  createdAt: v.optional(v.string()),
  updatedAt: v.optional(v.string()),
  needsReview: v.optional(v.boolean()),

  // Dosing
  dosing: v.optional(v.string()),
  dosingCleaned: v.optional(v.nullable(v.string())),
  dosingSummary: v.optional(v.string()),
  description_json: v.optional(v.any()),
  dosingSummary_json: v.optional(v.any()),

  // Patient Attribute
  patientAttribute: v.optional(v.string()),
  patientAttributeValue: v.optional(v.string()),

  // ICD-10 Mapping
  icd10AccuracyConfidence: v.optional(
    v.nullable(v.pipe(v.number(), v.minValue(0), v.maxValue(100))),
  ),
  icd10CoverageConfidence: v.optional(
    v.nullable(v.pipe(v.number(), v.minValue(0), v.maxValue(100))),
  ),
  icd10NoCodesFound: v.optional(v.boolean()),

  icd10Codes: v.optional(v.array(icd10CodeSchema)),
  hcpcsCodes: v.optional(v.array(v.string())),

  // Citations
  citations: v.optional(
    v.record(
      v.string(),
      v.union([
        v.string(),
        v.object({
          sourceDocumentId: v.optional(v.string()),
          citation: v.string(),
          url: v.optional(v.string()),
        }),
      ]),
    ),
  ),

  // Relations
  drugPolicyId: v.optional(v.string()),
  sourceDocument: v.optional(v.nullable(v.string())),
  sourceResearchFileId: v.optional(v.nullable(v.string())),

  // Optional Nested Relations
  dosingCalculations: v.optional(v.array(v.any())),
  defensibilityEntries: v.optional(v.array(v.any())),

  ...dosageCalculationSchema.entries,
})
export type Indication = v.InferOutput<typeof indicationSchema>

// Drug types
// ----------------------------------

/**
 * Format of the Jira issue key that anchors a drug's Content Pipeline work
 * (an initiative/epic in the configured `JIRA_CONTENT_PROJECT_KEY`).
 */
export const INITIATIVE_ID_PATTERN = /^(AT|CP)-\d+$/

export const drugSchema = v.object({
  setId: v.string(),
  brandName: v.string(),
  genericName: v.string(),
  rialticDrugName: v.optional(v.string()),
  effectiveTime: v.optional(v.string()),
  formulation: v.string(),
  manufacturer: v.string(),
  approvalYear: v.string(),
  description: v.string(),
  description_json: v.optional(v.any()),
  ndcCodes: v.array(v.string()),
  createdAt: v.string(),
  updatedAt: v.string(),
  // 'in-progress', 'needs-review', 'done' are DEPRECATED: retained only so
  // existing persisted drugs still validate; not user-selectable.
  status: v.picklist([
    'draft',
    'scoping',
    'design',
    'finalized',
    'in-progress',
    'needs-review',
    'done',
  ]),
  indications: v.optional(v.array(v.lazy(() => indicationSchema))),
  /**
   * Tool-agnostic reference to the drug's Content Pipeline initiative/epic
   * (currently a Jira issue key). Named `initiativeId`, not `jiraInitiativeId`,
   * so it stays valid if tracking ever moves off Jira. Every policy's "Policy
   * Build" epic is created as a child of this issue (TOOL-436).
   */
  initiativeId: v.optional(
    v.pipe(
      v.string(),
      v.regex(
        INITIATIVE_ID_PATTERN,
        'Initiative ID must look like AT-123 or CP-123',
      ),
    ),
  ),
})

export const drugListItemSchema = v.omit(drugSchema, [
  'indications',
  'description_json',
])
export type DrugListItem = v.InferOutput<typeof drugListItemSchema>
export type Drug = v.InferOutput<typeof drugSchema>
export type DrugStatus = Drug['status']

/**
 * One recorded stage transition (forward or backward) on the drug's status
 * workflow, so phase durations are reportable in-app (TOOL-583). `actor` is
 * a display name/email — best-effort from the WS message's user or the
 * `/finalize` request body, not a verified identity.
 */
export interface PhaseTransition {
  from: DrugStatus
  to: DrugStatus
  at: string
  actor: string
}

import type { AgentState } from '.'
import type {
  CategorizedCode,
  CategorizedIcd10Codes,
  SignalDot,
  SourceSignal,
} from './aggregation/categorizeIcd10Codes'
export declare namespace DrugBuilder {
  export type { AgentState }
  export type {
    ActivityFeedItem,
    ActivityFeedItemType,
    IndicationIcd10Code,
    IndicationStatus,
    Document,
    Indication,
    Drug,
    DrugStatus,
    PhaseTransition,
    TimePeriod,
    PayerClassification,
    PayerSource,
    CategorizedCode,
    CategorizedIcd10Codes,
    SignalDot,
    SourceSignal,
  }
}
